import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import { useAppTheme } from "../../theme/useAppTheme";

const FromHomeTab = ({ orderID, orderIds, onSuccess, uploadPath }) => {
  const [transactionId, setTransactionId] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const isFormValid = transactionId.trim() !== "" && selectedFile !== null;

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const fileSizeMB = file.size / (1024 * 1024);
    if (fileSizeMB > 5) {
      toast.error("File size should be less than 5 MB");
      return;
    }

    setSelectedFile(file);
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);

    try {
      const ids = orderIds?.length ? orderIds : [orderID];
      const token = localStorage.getItem("token");
      let message = "";

      for (let index = 0; index < ids.length; index += 1) {
        const formData = new FormData();
        formData.append(
          "transactionId",
          ids.length > 1 && index > 0 ? `${transactionId}-${index + 1}` : transactionId,
        );
        formData.append("paymentType", "Offline");
        formData.append("orderId", ids[index]);
        formData.append("document", selectedFile);

        const res = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}${
            uploadPath || "/api/v1/transactions/updatePaymentDetails"
          }`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              version: "1.2.0",
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const msg = res?.data?.errormsg;
        if (res?.data?.success === false || (msg && msg !== "")) {
          toast.error(msg || res?.data?.message || "Error uploading payment");
          setIsSubmitting(false);
          return;
        }
        message = res?.data?.message || message;
      }

      toast.success(
        uploadPath && message ? message : "Payment details uploaded successfully",
      );
      onSuccess?.();
      navigate("/OfflineTransactions");
      setIsSubmitting(false);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error uploading payment");
      setIsSubmitting(false);
    }
  };

  const themeColors = useAppTheme();

  return (
    <Box
      sx={{
        padding: 3,
        background: themeColors.mediumContainer,
        color: themeColors.text,
      }}
    >
      <Typography variant="h6" sx={{ textAlign: "center", mb: 3 }}>
        From Home
      </Typography>

      <Card
        sx={{
          padding: 3,
          background: themeColors.mediumContainer,
          color: themeColors.text,
        }}
      >
        <TextField
          fullWidth
          label="Transaction ID"
          variant="outlined"
          value={transactionId}
          onChange={(e) => setTransactionId(e.target.value)}
          inputProps={{ maxLength: 20 }}
          sx={{
            mb: 2,
            "& .MuiOutlinedInput-root": {
              background: themeColors.mediumContainer,
              color: themeColors.text,
            },
            "& .MuiInputLabel-root": {
              color: themeColors.text,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: themeColors.scaffold,
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              borderColor: themeColors.primary,
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              borderColor: themeColors.primary,
            },
          }}
        />

        <Box sx={{ height: 20 }} />

        <Button
          variant="contained"
          component="label"
          startIcon={<CloudUploadIcon />}
          sx={{
            width: "100%",
            background: themeColors.primary,
            color: themeColors.text,
            "&:hover": {
              background: themeColors.svgColor,
            },
            border: `1px solid ${themeColors.scaffold}`,
          }}
        >
          {selectedFile ? selectedFile.name : "Upload Payment Screenshot"}
          <input
            type="file"
            accept="image/*,application/pdf,.pdf"
            hidden
            onChange={handleFileChange}
          />
        </Button>

        <Box sx={{ height: 10 }} />

        <Typography variant="body2" sx={{ color: themeColors.text }}>
          Disclaimer : This app will collect and store the uploaded images to
          enable the jeweller to verify the payment to successfully complete the
          offline transaction.
        </Typography>

        <Box sx={{ height: 20 }} />

        <Button
          variant="contained"
          disabled={!isFormValid || isSubmitting}
          onClick={handleSubmit}
          fullWidth
          sx={{
            background: themeColors.primary,
            color: themeColors.text,

            "&:hover": {
              background: themeColors.svgColor,
            },

            "&.Mui-disabled": {
              background: themeColors.primary,
              color: themeColors.text,
              opacity: 0.6,
            },
            border: `1px solid ${themeColors.scaffold}`,
            fontWeight: "bold",
            py: 1.5,
            fontSize: "1rem",
            textTransform: "none",
          }}
        >
          {isSubmitting ? <CircularProgress size={22} /> : "Submit"}
        </Button>
      </Card>
    </Box>
  );
};

export default FromHomeTab;
